// Компонент для проверки товаров при получении
// Валидация кодов отправляется на сервер, фронт только матчит и показывает

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    LinearProgress,
    IconButton,
    InputAdornment,
    Chip,
    Collapse,
    Divider,
    Tooltip,
    Checkbox,
    Button
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    QrCodeScanner as QrCodeScannerIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    Check as CheckIcon,
    Warning as WarningIcon,
    DeleteSweep as DeleteSweepIcon,
    Search as SearchIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { ReceivingVerificationRow } from './types';
import { convertRuToEn } from './keyboardMapping';

const MARK_CODE_LENGTH = 31;

// ===== Серии: парсинг и группировка =====
interface GroupedSeries {
    key: string;
    full: string;
    serial: string;
    expiry?: string;
    count: number;
}

const parseSeriesList = (raw: any): any[] => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw !== 'string' || !raw.trim() || raw === '[]') return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        if (typeof parsed === 'string') return [parsed];
        return [];
    } catch {
        return [raw];
    }
};

const getSeriesLabel = (item: any): string =>
    (typeof item === 'string' ? item : (item?.name || '')).trim();

// "Серия, СрокГодности; Сертификат" → группировка по серии + сроку годности
const groupSeries = (list: any[]): GroupedSeries[] => {
    const map = new Map<string, GroupedSeries>();
    for (const item of list) {
        const full = getSeriesLabel(item);
        if (!full) continue;
        let working = full;
        const semiIdx = working.indexOf(';');
        if (semiIdx !== -1) working = working.substring(0, semiIdx).trim();
        let serial = working;
        let expiry: string | undefined;
        const commaIdx = working.indexOf(',');
        if (commaIdx !== -1) {
            serial = working.substring(0, commaIdx).trim();
            expiry = working.substring(commaIdx + 1).trim() || undefined;
        }
        const key = `${serial}__${expiry || ''}`;
        const existing = map.get(key);
        if (existing) existing.count += 1;
        else map.set(key, { key, full, serial, expiry, count: 1 });
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
};

interface ProductVerificationViewProps {
    products: ReceivingVerificationRow[];
    onProductUpdate: (productId: string, field: 'actualQuantity' | 'actualMarkCodes', value: string | string[]) => void;
    readOnly?: boolean;
    errorProductIds?: Set<string>;
    /** Режим самоконтроля (приёмка на складе): чекбоксы, группировка серий, поиск. Только фронт, на сервер не отправляется */
    selfCheck?: boolean;
}

const ProductVerificationView: React.FC<ProductVerificationViewProps> = ({
    products,
    onProductUpdate,
    readOnly = false,
    errorProductIds,
    selfCheck = false
}) => {
    const [scanInput, setScanInput] = useState('');
    const [validationError, setValidationError] = useState('');
    const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // ===== Самоконтроль (только локальное состояние) =====
    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    const toggleChecked = useCallback((id: string) => {
        setCheckedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    // Предрасчёт серий для каждого товара
    const seriesByProduct = useMemo(() => {
        const map = new Map<string, { raw: any[]; groups: GroupedSeries[] }>();
        products.forEach(p => {
            const raw = parseSeriesList((p as any).series);
            map.set(p.id, { raw, groups: groupSeries(raw) });
        });
        return map;
    }, [products]);

    // Поиск: наименование / код / штрихкод / серия / КМ
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const latinQuery = convertRuToEn(searchQuery.trim()).toLowerCase();
    const matchesQuery = useCallback((text?: string | null): boolean => {
        if (!normalizedQuery || !text) return false;
        const t = String(text).toLowerCase();
        return t.includes(normalizedQuery) || (!!latinQuery && t.includes(latinQuery));
    }, [normalizedQuery, latinQuery]);

    const searchMeta = useMemo(() => {
        const meta = new Map<string, { visible: boolean; deepMatch: boolean }>();
        products.forEach(p => {
            if (!selfCheck || !normalizedQuery) {
                meta.set(p.id, { visible: true, deepMatch: false });
                return;
            }
            const baseMatch = matchesQuery(p.MaterialName) || matchesQuery(p.MaterialCode) || matchesQuery(p.Barcode);
            const seriesMatch = (seriesByProduct.get(p.id)?.groups || []).some(g => matchesQuery(g.full));
            const kmMatch = (p.actualMarkCodes || []).some(c => matchesQuery(c))
                || (p.markCodes || []).some(mc => matchesQuery(mc.mark));
            meta.set(p.id, { visible: baseMatch || seriesMatch || kmMatch, deepMatch: seriesMatch || kmMatch });
        });
        return meta;
    }, [products, selfCheck, normalizedQuery, matchesQuery, seriesByProduct]);

    const visibleProducts = products.filter(p => searchMeta.get(p.id)?.visible !== false);
    const checkedCount = products.filter(p => checkedIds.has(p.id)).length;
    const allChecked = products.length > 0 && checkedCount === products.length;
    const visibleCheckedCount = visibleProducts.filter(p => checkedIds.has(p.id)).length;
    const allVisibleChecked = visibleProducts.length > 0 && visibleCheckedCount === visibleProducts.length;

    const toggleAllVisible = () => {
        setCheckedIds(prev => {
            const next = new Set(prev);
            if (allVisibleChecked) visibleProducts.forEach(p => next.delete(p.id));
            else visibleProducts.forEach(p => next.add(p.id));
            return next;
        });
    };

    const columnCount = selfCheck ? 6 : 5;

    // Авто-фокус
    useEffect(() => {
        if (inputRef.current && !readOnly) {
            inputRef.current.focus();
        }
    }, [readOnly]);


    // Авто-раскрытие товаров с ошибками
    useEffect(() => {
        if (errorProductIds && errorProductIds.size > 0) {
            const firstError = Array.from(errorProductIds)[0];
            setExpandedProduct(firstError);
        }
    }, [errorProductIds]);

    // Глобальный набор дубликатов
    const duplicateCodes = useMemo(() => {
        const allCodes: string[] = [];
        products.forEach(p => {
            p.actualMarkCodes?.forEach(c => {
                if (c && c.trim()) allCodes.push(c.trim());
            });
        });
        const seen = new Set<string>();
        const dupes = new Set<string>();
        allCodes.forEach(c => {
            if (seen.has(c)) dupes.add(c);
            seen.add(c);
        });
        return dupes;
    }, [products]);

    // Статистика
    const totalCodes = products.reduce((acc, p) => acc + (p.markCodes?.length || 0), 0);
    const scannedCodes = products.reduce((acc, p) =>
        acc + (p.actualMarkCodes?.filter(c => c && c.trim().length > 0).length || 0), 0
    );
    const progress = totalCodes > 0 ? (scannedCodes / totalCodes) * 100 : 0;
    const allDone = scannedCodes >= totalCodes && totalCodes > 0;

    // Конвертация кирилицы → латиница
    const convertInput = useCallback((value: string): string => {
        if (!value) return '';
        return convertRuToEn(value);
    }, []);

    // Обработка сканирования: обрезка → дубликат → exact match → GTIN match → fallback
    const handleSubmitCode = useCallback(() => {
        if (!scanInput.trim()) return;

        const converted = convertRuToEn(scanInput);
        const code = converted.slice(0, MARK_CODE_LENGTH);

        if (code.length < MARK_CODE_LENGTH) {
            setValidationError(`Код слишком короткий: ${code.length} символов (нужно ${MARK_CODE_LENGTH})`);
            return;
        }

        // 1. Проверяем дубликат среди уже отсканированных
        const isDuplicate = products.some(p =>
            p.actualMarkCodes?.some(c => c === code)
        );
        if (isDuplicate) {
            setValidationError('Этот код уже отсканирован');
            setScanInput('');
            return;
        }

        // 2. Ищем товар
        let foundProduct: ReceivingVerificationRow | undefined;

        // 2a. Точное совпадение с ожидаемым mark
        foundProduct = products.find(p =>
            p.markCodes?.some(mc => mc.mark === code) &&
            (p.actualMarkCodes || []).some(c => !c || c.trim() === '')
        );

        // 2b. Нечёткий матч: prefix + suffix от ожидаемого mark
        // Если mark из API = "015555***5555", то prefix = "015555", suffix = "5555"
        // Сканированный код должен начинаться на prefix, заканчиваться на suffix,
        // и соответствовать GS1 формату (начинается на "01", содержит "21")
        if (!foundProduct) {
            foundProduct = products.find(p => {
                if (!p.markCodes?.length) return false;
                const hasEmptySlot = (p.actualMarkCodes || []).some(c => !c || c.trim() === '');
                if (!hasEmptySlot) return false;

                return p.markCodes.some(mc => {
                    const mark = mc.mark || '';
                    if (!mark.includes('*') && mark.length === MARK_CODE_LENGTH) return false; // exact match уже проверен выше

                    // Разбиваем по * на prefix и suffix
                    const starIndex = mark.indexOf('*');
                    const lastStarIndex = mark.lastIndexOf('*');
                    if (starIndex === -1) return false;

                    const prefix = mark.substring(0, starIndex);
                    const suffix = mark.substring(lastStarIndex + 1);

                    // Проверяем GS1 формат + prefix/suffix
                    const hasGS1Structure = code.startsWith('01') && code.includes('21');
                    const matchesPrefix = prefix.length > 0 && code.startsWith(prefix);
                    const matchesSuffix = suffix.length > 0 && code.endsWith(suffix);

                    return hasGS1Structure && matchesPrefix && matchesSuffix;
                });
            });
        }

        // 2c. GTIN-матчинг: символы [2..16] из кода = 14-значный GTIN
        if (!foundProduct && code.length >= 16) {
            const gtinFromCode = code.substring(2, 16); // 14 цифр
            foundProduct = products.find(p => {
                if (!p.Barcode || !p.markCodes?.length) return false;
                const hasEmptySlot = (p.actualMarkCodes || []).some(c => !c || c.trim() === '');
                if (!hasEmptySlot) return false;
                const paddedBarcode = p.Barcode.padStart(14, '0');
                return gtinFromCode === paddedBarcode || gtinFromCode === p.Barcode;
            });
        }

        // 3. Нет подходящего товара
        if (!foundProduct) {
            setValidationError('Код не принадлежит ни одному товару в этом документе. Проверьте, что сканируете правильную упаковку');
            setScanInput('');
            return;
        }

        // 4. Записываем код в первый пустой слот
        const currentCodes = foundProduct.actualMarkCodes || [];
        const firstEmptyIndex = currentCodes.findIndex(c => !c || c.trim() === '');

        const newMarkCodes = [...currentCodes];
        newMarkCodes[firstEmptyIndex] = code;
        onProductUpdate(foundProduct.id, 'actualMarkCodes', newMarkCodes);

        setExpandedProduct(foundProduct.id);
        setValidationError('');
        setScanInput('');

        // Вернуть фокус
        setTimeout(() => inputRef.current?.focus(), 50);
    }, [scanInput, products, onProductUpdate]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmitCode();
        }
    }, [handleSubmitCode]);

    // Глобальный перехват клавиатуры (сканер работает без фокуса на поле)
    useEffect(() => {
        if (readOnly) return;

        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            // Не перехватывать системные комбинации
            if (e.ctrlKey || e.altKey || e.metaKey) return;
            if (e.key === 'Tab' || e.key === 'Escape') return;

            // Если фокус уже в нашем поле - ничего не делаем
            if (document.activeElement === inputRef.current) return;

            // Если фокус в другом текстовом поле - не перехватываем
            const tag = (document.activeElement as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

            if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmitCode();
                return;
            }

            // Печатный символ → перенаправить в поле
            if (e.key.length === 1) {
                e.preventDefault();
                inputRef.current?.focus();
                setScanInput(prev => prev + convertRuToEn(e.key));
            }
        };

        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }, [readOnly, handleSubmitCode]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const converted = convertInput(raw);
        setScanInput(converted);
        setValidationError('');
    }, [convertInput]);

    // Ручной ввод в конкретный слот
    const handleManualChange = (productId: string, index: number, value: string) => {
        const converted = convertRuToEn(value).slice(0, MARK_CODE_LENGTH);
        const product = products.find(p => p.id === productId);
        if (!product) return;

        const newMarkCodes = [...(product.actualMarkCodes || [])];
        newMarkCodes[index] = converted;
        onProductUpdate(productId, 'actualMarkCodes', newMarkCodes);
    };

    // Удалить код
    const removeCode = (productId: string, index: number) => {
        const product = products.find(p => p.id === productId);
        if (!product) return;

        const newMarkCodes = [...(product.actualMarkCodes || [])];
        newMarkCodes[index] = '';
        onProductUpdate(productId, 'actualMarkCodes', newMarkCodes);

        setTimeout(() => inputRef.current?.focus(), 50);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Панель сканирования (фиксированная сверху) */}
            {!readOnly && (
                <Box sx={{
                    p: 2.5, zIndex: 10, borderRadius: 0,
                    borderBottom: '1px solid #e2e8f0',
                    bgcolor: allDone ? '#f0fdf4' : '#fafafa',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: allDone ? 'success.main' : 'primary.main' }}>
                            {allDone ? <CheckIcon fontSize="small" /> : <QrCodeScannerIcon fontSize="small" />}
                            <Typography variant="subtitle2" fontWeight="bold">
                                Маркировка
                            </Typography>
                        </Box>

                        <Chip
                            label={`${scannedCodes} / ${totalCodes}`}
                            size="small"
                            color={allDone ? "success" : "default"}
                            variant={allDone ? "filled" : "outlined"}
                            sx={{ fontWeight: 'bold', height: 24 }}
                        />
                    </Box>

                    {/* Прогресс-бар */}
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        color={allDone ? "success" : "primary"}
                        sx={{ mb: 2, height: 4, borderRadius: 2, bgcolor: 'grey.200' }}
                    />

                    {/* Поле ввода - как в MarkCodesInput */}
                    {!allDone && (
                        <TextField
                            fullWidth
                            size="small"
                            inputRef={inputRef}
                            label={`Сканирование кода (${scannedCodes + 1} из ${totalCodes})`}
                            placeholder="Отсканируйте код и нажмите Enter"
                            value={scanInput}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            error={!!validationError}
                            helperText={
                                validationError
                                    ? validationError
                                    : (scanInput.length > 0
                                        ? `${scanInput.length} символов → будет обрезан до ${MARK_CODE_LENGTH}`
                                        : '')
                            }
                            InputProps={{
                                style: {
                                    fontFamily: 'monospace',
                                    fontSize: '0.9rem',
                                },
                                endAdornment: scanInput.length > 0 && (
                                    <InputAdornment position="end">
                                        <Chip label="Enter" size="small" onClick={handleSubmitCode} sx={{ cursor: 'pointer' }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                        borderWidth: 2
                                    }
                                }
                            }}
                        />
                    )}
                </Box>
            )}

            {/* Панель самоконтроля: поиск + счётчик проверенных */}
            {selfCheck && (
                <Box sx={{
                    px: 2.5, py: 1.5,
                    borderBottom: '1px solid #e2e8f0',
                    bgcolor: '#fff',
                    display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'
                }}>
                    <TextField
                        id="storage-receiving-search"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Поиск: наименование, серия, КМ, штрихкод"
                        sx={{ flex: 1, minWidth: 260 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchQuery('')} id="storage-receiving-search-clear">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    {normalizedQuery && (
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            Найдено: {visibleProducts.length}
                        </Typography>
                    )}
                    <Chip
                        icon={<CheckIcon style={{ fontSize: 14 }} />}
                        label={`Проверено ${checkedCount} / ${products.length}`}
                        size="small"
                        color={allChecked ? 'success' : 'default'}
                        variant={allChecked ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600 }}
                    />
                    {checkedCount > 0 && (
                        <Button
                            id="storage-receiving-uncheck-all"
                            size="small"
                            color="inherit"
                            onClick={() => setCheckedIds(new Set())}
                            sx={{ color: 'text.secondary', textTransform: 'none' }}
                        >
                            Снять отметки
                        </Button>
                    )}
                </Box>
            )}

            {/* Список товаров */}
            <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {selfCheck && (
                                <TableCell padding="checkbox" sx={{ bgcolor: '#f8fafc' }}>
                                    <Tooltip title={allVisibleChecked ? 'Снять отметки' : 'Отметить все'}>
                                        <Checkbox
                                            size="small"
                                            color="success"
                                            checked={allVisibleChecked}
                                            indeterminate={visibleCheckedCount > 0 && !allVisibleChecked}
                                            onChange={toggleAllVisible}
                                            disabled={visibleProducts.length === 0}
                                        />
                                    </Tooltip>
                                </TableCell>
                            )}
                            <TableCell width="5%" sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>№</TableCell>
                            <TableCell width="40%" sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>НАИМЕНОВАНИЕ</TableCell>
                            <TableCell width="15%" sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>ШТРИХКОД</TableCell>
                            <TableCell width="10%" align="center" sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>КОЛ-ВО</TableCell>
                            <TableCell width="30%" align="center" sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>МАРКИРОВКА</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {selfCheck && normalizedQuery && visibleProducts.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={columnCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    Ничего не найдено по запросу «{searchQuery.trim()}»
                                </TableCell>
                            </TableRow>
                        )}
                        {products.map((product, index) => {
                            const meta = searchMeta.get(product.id);
                            if (meta && !meta.visible) return null;

                            const expected = product.markCodes?.length || 0;
                            const current = product.actualMarkCodes?.filter(c => c && c.trim().length > 0).length || 0;
                            const isComplete = current >= expected && expected > 0;
                            const hasNoMarks = expected === 0;
                            const isChecked = selfCheck && checkedIds.has(product.id);
                            // При поиске по серии/КМ - раскрываем позицию автоматически
                            const isExpanded = expandedProduct === product.id || (selfCheck && !!meta?.deepMatch);

                            // Серии товара
                            const seriesInfo = seriesByProduct.get(product.id);
                            const parsedSeries = seriesInfo?.raw || [];
                            const seriesGroups = seriesInfo?.groups || [];
                            const hasSeries = parsedSeries.length > 0;
                            const isExpandable = expected > 0 || hasSeries;

                            const qty = parseFloat(product.Quantity) || 0;
                            const qtyPcs = parseFloat(product.QuantityPcs || '0') || 0;
                            const quantityParts: string[] = [];
                            if (qty > 0) quantityParts.push(`${product.Quantity} уп`);
                            if (qtyPcs > 0) quantityParts.push(`${product.QuantityPcs} шт`);
                            const quantityString = quantityParts.length > 0 ? quantityParts.join(', ') : '-';

                            const hasError = errorProductIds?.has(product.id);

                            return (
                                <React.Fragment key={product.id}>
                                    <TableRow
                                        hover
                                        selected={isExpanded && !isChecked}
                                        onClick={() => isExpandable && setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                                        sx={{
                                            cursor: isExpandable ? 'pointer' : 'default',
                                            bgcolor: hasError
                                                ? 'rgba(211,47,47,0.15)'
                                                : isChecked
                                                    ? 'rgba(16,185,129,0.14)'
                                                    : isComplete && !selfCheck ? 'rgba(16,185,129,0.06)' : 'inherit',
                                            ...(isChecked && !hasError && {
                                                '&:hover': { bgcolor: 'rgba(16,185,129,0.2) !important' },
                                                '& > td:first-of-type': { boxShadow: 'inset 4px 0 0 #10b981' }
                                            }),
                                            borderLeft: hasError ? '5px solid' : 'none',
                                            borderLeftColor: hasError ? 'error.main' : 'transparent',
                                            transition: 'background-color 0.2s',
                                            ...(hasError && {
                                                animation: 'error-pulse 2s ease-in-out infinite',
                                                '@keyframes error-pulse': {
                                                    '0%, 100%': { bgcolor: 'rgba(211,47,47,0.15)' },
                                                    '50%': { bgcolor: 'rgba(211,47,47,0.08)' },
                                                }
                                            })
                                        }}
                                    >
                                        {selfCheck && (
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    id={`storage-receiving-check-${product.id}`}
                                                    size="small"
                                                    color="success"
                                                    checked={isChecked}
                                                    onChange={() => toggleChecked(product.id)}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{index + 1}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {product.MaterialName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                                {product.MaterialCode}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">{product.Barcode}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2" fontWeight="medium">{quantityString}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {expected > 0 ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                    <Chip
                                                        label={`${current} / ${expected}`}
                                                        color={isComplete ? "success" : current > 0 ? "warning" : "default"}
                                                        size="small"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                    {isExpanded ? <KeyboardArrowUpIcon color="action" /> : <KeyboardArrowDownIcon color="action" />}
                                                </Box>
                                            ) : hasSeries ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                    <Chip label="Серии" size="small" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
                                                    {isExpanded ? <KeyboardArrowUpIcon color="action" /> : <KeyboardArrowDownIcon color="action" />}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">Без маркировки</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>

                                    {/* Развёрнутые коды - стиль как в MarkCodesInput */}
                                    <TableRow>
                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
                                            <Collapse in={isExpanded && isExpandable} timeout="auto" unmountOnExit>
                                                <Box sx={{
                                                    p: 2, my: 1,
                                                    border: '1px solid',
                                                    borderColor: isComplete ? 'success.light' : '#e0e0e0',
                                                    borderRadius: 1,
                                                    bgcolor: isComplete ? 'success.50' : '#fafafa',
                                                    transition: 'all 0.3s'
                                                }}>
                                                    {/* Заголовок с прогрессом */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <QrCodeScannerIcon sx={{ fontSize: 16, color: isComplete ? 'success.main' : 'primary.main' }} />
                                                            <Typography variant="subtitle2" fontWeight="bold">
                                                                Коды маркировки
                                                            </Typography>
                                                            <Chip
                                                                label={`${current} / ${expected}`}
                                                                size="small"
                                                                color={isComplete ? "success" : "default"}
                                                                variant={isComplete ? "filled" : "outlined"}
                                                                sx={{ fontWeight: 'bold', height: 24 }}
                                                            />
                                                        </Box>
                                                    </Box>

                                                    {/* Прогресс-бар товара */}
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={expected > 0 ? (current / expected) * 100 : 0}
                                                        color={isComplete ? "success" : "primary"}
                                                        sx={{ mb: 2, height: 4, borderRadius: 2, bgcolor: 'grey.200' }}
                                                    />

                                                    {/* Список кодов */}
                                                    {current > 0 && (
                                                        <>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                {readOnly ? 'Коды:' : 'Введённые коды:'}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflow: 'auto' }}>
                                                                {product.actualMarkCodes?.map((code, idx) => {
                                                                    if (!code || code.trim() === '') return null;
                                                                    const isDuplicate = duplicateCodes.has(code);
                                                                    const expectedMark = product.markCodes?.[idx]?.mark || '';
                                                                    const isMatch = expectedMark && expectedMark === code;
                                                                    const isSearchHit = selfCheck && matchesQuery(code);

                                                                    return (
                                                                        <Box
                                                                            key={idx}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 1,
                                                                                py: 0.5,
                                                                                px: 1,
                                                                                borderRadius: 1,
                                                                                bgcolor: isDuplicate ? '#fff5f5' : isSearchHit ? '#fef9c3' : 'white',
                                                                                border: isDuplicate ? '2px solid' : '1px solid',
                                                                                borderColor: isDuplicate ? 'error.main' : 'success.light',
                                                                                animation: isDuplicate ? 'pulse-error 1.5s ease-in-out infinite' : 'none',
                                                                                '@keyframes pulse-error': {
                                                                                    '0%, 100%': { borderColor: 'error.main' },
                                                                                    '50%': { borderColor: 'error.light' },
                                                                                },
                                                                                '&:hover': { borderColor: 'error.light' }
                                                                            }}
                                                                        >
                                                                            {isDuplicate
                                                                                ? <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                                                                : <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                                                            }
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    fontFamily: 'monospace',
                                                                                    fontSize: '0.75rem',
                                                                                    flexGrow: 1,
                                                                                    color: isDuplicate ? 'error.main' : 'text.primary',
                                                                                    fontWeight: isDuplicate ? 'bold' : 'normal',
                                                                                    letterSpacing: 0.3
                                                                                }}
                                                                            >
                                                                                №{idx + 1}: {code} {isDuplicate && '⚠ ДУБЛЬ'}
                                                                            </Typography>
                                                                            {!readOnly && (
                                                                                <Tooltip title="Удалить код">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        color="error"
                                                                                        onClick={(e) => { e.stopPropagation(); removeCode(product.id, idx); }}
                                                                                        sx={{ p: 0.25 }}
                                                                                    >
                                                                                        <DeleteSweepIcon sx={{ fontSize: 14 }} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        </>
                                                    )}

                                                    {/* Незаполненные слоты */}
                                                    {!isComplete && !readOnly && expected > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                            Осталось: {expected - current} код(ов). Отсканируйте в поле выше.
                                                        </Typography>
                                                    )}

                                                    {/* Серии товара */}
                                                    {hasSeries && (
                                                        <Box sx={{ mt: expected > 0 ? 2 : 0, pt: expected > 0 ? 2 : 0, borderTop: expected > 0 ? '1px dashed #e0e0e0' : 'none' }}>
                                                            <Typography variant="caption" sx={{ mb: 1, fontWeight: 700, color: 'info.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                {selfCheck
                                                                    ? `СЕРИИ (${seriesGroups.length} ${seriesGroups.length === 1 ? 'вид' : 'видов'}, всего ${parsedSeries.length} шт)`
                                                                    : `СЕРИИ (${parsedSeries.length})`}
                                                            </Typography>
                                                            {selfCheck ? (
                                                                // Сгруппированные серии: «Серия - N шт»
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                    {seriesGroups.map(g => {
                                                                        const hit = matchesQuery(g.full);
                                                                        return (
                                                                            <Box
                                                                                key={g.key}
                                                                                sx={{
                                                                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                                                                    px: 1.25, py: 0.5,
                                                                                    borderRadius: 1,
                                                                                    border: '1px solid',
                                                                                    borderColor: hit ? '#facc15' : '#e2e8f0',
                                                                                    bgcolor: hit ? '#fef9c3' : '#fff'
                                                                                }}
                                                                            >
                                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, flexGrow: 1 }}>
                                                                                    {g.serial}
                                                                                    {g.expiry && (
                                                                                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 1, fontFamily: 'inherit' }}>
                                                                                            до {g.expiry}
                                                                                        </Box>
                                                                                    )}
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={`${g.count} шт`}
                                                                                    size="small"
                                                                                    color="info"
                                                                                    variant={g.count > 1 ? 'filled' : 'outlined'}
                                                                                    sx={{ fontWeight: 700, minWidth: 56 }}
                                                                                />
                                                                            </Box>
                                                                        );
                                                                    })}
                                                                </Box>
                                                            ) : (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {parsedSeries.map((s, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={getSeriesLabel(s)}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="info"
                                                                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 500 }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ProductVerificationView;
